import {Component, EventEmitter, HostListener, Input, OnInit, Output, ViewChild} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {TransactionService} from 'src/app/services/transaction.service';
import {CategoryService} from 'src/app/services/category.service';
import {TranslateService} from '@ngx-translate/core';
import {MatPaginator, PageEvent} from '@angular/material/paginator';
import {NotificationService} from '../../../services/notification.service';
import {animate, state, style, transition, trigger} from '@angular/animations';
import {MatSnackBar} from '@angular/material/snack-bar';
import {CurrencyService} from '../../../services/currency-service';
import {TransactionDTO} from '../../../model/transactionDTO';
import {ClientService} from '../../../services/client.service';
import {ClientDTO} from '../../../model/clientDTO';

interface UnassignedTransactionDTO extends TransactionDTO {
  openPicker: boolean;
  hidden: boolean;
  search: string;
}

@Component({
  selector: 'app-transaction-unassigned',
  animations: [
    trigger('expandCollapse', [
      state('expandCollapseState', style({height: '*'})),
      transition('* => void', [style({height: '*'}), animate('300ms', style({height: '0'}))]),
      transition('void => *', [style({height: '0'}), animate('300ms', style({height: '*'}))])
    ]),
    trigger('items', [
      transition(':leave', [
        animate(
          '300ms 300ms',
          style({
            height: '0',
          })
        )
      ])
    ])
  ],
  templateUrl: './transaction-unassigned.component.html',
  styleUrls: ['./transaction-unassigned.component.scss', '../shared-transaction.scss']
})

export class TransactionUnassignedComponent implements OnInit {

  @Output()
  change: EventEmitter<number> = new EventEmitter<number>();

  @Output()
  undoAssignment: EventEmitter<boolean> = new EventEmitter<boolean>();

  @Output()
  assign: EventEmitter<boolean> = new EventEmitter<boolean>();

  @Input()
  accountId: number;

  pageSize = 25;

  enterPressed: EventEmitter<boolean> = new EventEmitter<boolean>();

  @ViewChild('paginator') paginator: MatPaginator;

  public transactions: UnassignedTransactionDTO[];
  public openPayments: number[] = [];

  length = 0;

  pageEvent: PageEvent = new PageEvent;
  categories = {debit: [], credit: []};

  /*
  * filtering
  * */
  sortDirectionOptions = [
    {value: 'asc', label: 'oplopend'},
    {value: 'desc', label: 'aflopend'}
  ];
  sortColumnOptions = [
    {value: 'value_date', label: 'datum'},
    {value: 'iban', label: 'IBAN'},
    {value: 'calculated_amount', label: 'bedrag'}
  ];

  sortColumn = 'iban';
  sortDirection = 'desc';
  search = '';
  client: ClientDTO;

  constructor(
    private clientService: ClientService,
    private route: ActivatedRoute,
    private router: Router,
    private transactionService: TransactionService,
    private categoryService: CategoryService,
    private notificationService: NotificationService,
    private snackBar: MatSnackBar,
    public currencyService: CurrencyService,
    public translate: TranslateService) {
    translate.use('nl');
  }

  ngOnInit() {
    this.init();
  }

  init() {
    this.getTransactions();
    this.getSubcategories();
    this.clientService.getByAccountId(this.accountId)
      .subscribe(client => this.client = client);
  }

  getTransactions(event?: PageEvent) {
    this.transactionService.getUnassigned(this.accountId, event?.pageIndex ? event.pageIndex : 0, this.pageSize, this.sortColumn, this.sortDirection, this.search).subscribe((data) => {
      this.transactions = data.content as UnassignedTransactionDTO[];
      this.length = data.totalElements;
      this.change.emit(this.length);
      if (data.content.length !== 0) {
        this.open(data.content[0]);
      }
    });
    return event;
  }

  getSubcategories() {
    this.categoryService.getGroupedSubcategories().subscribe(data => {
      this.categories.credit = this.sortByKey(data['types']['CREDIT']);
      this.categories.debit = this.sortByKey(data['types']['DEBIT']);
    });
  }

  save($event, transaction, method) {
    transaction.hidden = true;
    transaction.openPicker = false;
    setTimeout(() => {
      if (method === 'SELECTION' && transaction.suggestions?.length > 0) {
        method = 'SUGGESTION_IGNORED';
      }
      const assignment = {transactionId: transaction.id, subcategoryId: $event, method: method};
      this.transactionService.assignSubcategory(assignment).subscribe(() => {
        this.assign.emit(true);
        this.showUndoButton(transaction.id);
        this.getTransactions(this.pageEvent);
      });
    }, 650);
  }

  showUndoButton(transactionId) {
    this.snackBar.open('transactie ingedeeld', 'undo', {
      duration: 4000,
    }).onAction().subscribe(() => {
      this.transactionService.withdrawAssignment(transactionId).subscribe(() => {
        this.getTransactions(this.pageEvent);
        this.undoAssignment.emit(true);
      });
    });
  }

  open(transaction) {
    this.openPayments.push(transaction.id);
  }

  shouldOpen(transaction) {
    return this.openPayments.indexOf(transaction.id) !== -1;
  }

  filter() {
    this.getTransactions(this.pageEvent);
  }

  sortByKey(toBeSorted) {
    const keys = [];
    for (const k in toBeSorted) {
      if (toBeSorted.hasOwnProperty(k)) {
        keys.push(k);
      }
    }
    keys.sort();
    keys.splice(keys.indexOf('Overig'), 1);
    keys.push('Overig');
    const sorted = [];
    for (let i = 0, len = keys.length; i < len; i++) {
      sorted[i] = {key: keys[i], value: toBeSorted[keys[i]]};
    }
    return sorted;
  }

  keydown($event: KeyboardEvent) {
    if ($event.key === 'Enter') {
      this.enterPressed.emit(true);
    }
  }

  onClickedOutside($event) {
    const element = $event.target.attributes.getNamedItem('data-subcategory-trigger');
    if (!(element !== null && element.value === 'true')) {
      this.closeSubcategoryPicker();
    }
  }

  @HostListener('document:keydown.escape', ['$event'])
  onKeydownHandler() {
    this.closeSubcategoryPicker();
  }

  hasSuggestions(transaction: any) {
    return transaction.suggestions && transaction.suggestions?.length !== 0;
  }

  private closeSubcategoryPicker() {
    for (const transaction of this.transactions) {
      transaction.openPicker = false;
    }
  }
}
